<template>
    <!--
        The navbar strip: `h-(--ui-header-height)` with a bottom hairline, the page TITLE at the
        left and the controls at the right. The title is the current section's menu label
        (`navTitleKey`), so it is the same word the sidebar shows and no new copy is introduced.

        It paints `--ui-bg-chrome` — the same surface the sidebar takes, so THE FRAME IS ONE
        SURFACE: white over the neutral-100 canvas in light, and in dark the panel colour, the
        same mix the cards take, over the darker neutral-900 canvas. The hairline below it is
        the seam, not the separation.

        The user menu is NOT here any more — it lives at the bottom of the sidebar, where an
        admin console puts it. What stays is what belongs to the whole window rather than to the
        person: the light/dark toggle (vue-spec `theme.md`), and the portal's request indicator.
    -->
    <header class="relative h-(--ui-header-height) shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-default bg-(--ui-bg-chrome)">
        <!--
            The one thing this project adds to the house topbar: the in-flight request bar.
            Every mutation in the portal brackets itself with `startLoading` / `stopLoading`,
            and this hairline at the top of the chrome is where that count is shown. It is
            absolutely positioned, so it costs the row no height and moves nothing.
        -->
        <UProgress v-if="appStore.isLoaderActive()" size="xs" class="absolute inset-x-0 top-0" />

        <div class="flex items-center gap-2 min-w-0">
            <!-- Opens the drawer. It does not have to close it: once open, the drawer covers
                 this corner, and the X inside it and the backdrop are the ways out. -->
            <UButton
                v-if="isMobile"
                color="neutral"
                variant="ghost"
                icon="i-lucide-menu"
                :aria-label="$t('layout.open_menu')"
                data-testid="nav-menu"
                @click="openMenu"
            />
            <h1 class="font-semibold text-highlighted truncate">{{ $t(titleKey) }}</h1>
        </div>

        <div class="flex items-center shrink-0 gap-1">
            <ColorModeToggle />
        </div>
    </header>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useRoute } from 'vue-router'

    import ColorModeToggle from '@/layouts/components/ColorModeToggle.vue'
    import { navTitleKey, useLayout } from '@/layouts/layout'
    import { useAppStore } from '@/stores/app'

    const route = useRoute()
    const appStore = useAppStore()
    const { isMobile, openMenu } = useLayout()

    const titleKey = computed((): string => navTitleKey(String(route.name ?? '')))
</script>
